import { Box, Button, LinearProgress, Typography } from "@material-ui/core";
import { Club } from "api/clubs";
import { patchSettings, UserProfile } from "api/user";
import { View } from "features/global/reducer";
import React, { useState } from "react";
import i18n from "../i18n";

// prop types
interface OnboardingProps {
    club: Club | null,
    userProfile: UserProfile | null,
    onFetchUserProfile: () => void,
    onNavigateTo: ({ view, tab }: { view: View, tab?: number }) => void,
    onShowEndDemoDialog: () => void,
}

// onboarding menu
const Onboarding = ({ club, userProfile, onFetchUserProfile, onNavigateTo, onShowEndDemoDialog }: OnboardingProps) => {
    // ui state
    const [showDesc, setShowDesc] = useState(false);
    const isCompany = club?.capabilities.includes("company");

    // no onboarding?
    if (!club || !userProfile) return null;
    const { userRole } = userProfile.memberships.find((m) => m.club.id === club.id)!;
    const { onboardingStep } = userProfile;
    if (!isCompany && (!(i18n.onboardingSteps[userRole] && i18n.onboardingSteps[userRole][onboardingStep]))) return null;
    if (isCompany && (!(i18n.companyOnboardingSteps[userRole] && i18n.companyOnboardingSteps[userRole][onboardingStep]))) return null;

    // handle step click
    const onStepClick = () => {
        setShowDesc(true);
        if (isCompany) {
            if (userRole === "owner") {
                if (onboardingStep === 0) {
                    onNavigateTo({ view: View.Club });
                } else if (onboardingStep === 1) {
                    onNavigateTo({ view: View.Members });
                } else if (onboardingStep === 2) {
                    onFetchUserProfile(); // reload courts for calendar
                    onNavigateTo({ view: View.Calendar });
                } else if (onboardingStep === 3) {
                    setShowDesc(false);
                    onShowEndDemoDialog();
                } else if (onboardingStep === 4) {
                    onNavigateTo({ view: View.Club, tab: 3 }); // ownership tab
                }
            } else if (userRole === "admin") {
                if (onboardingStep === 0) {
                    onNavigateTo({ view: View.Members });
                } else if (onboardingStep === 1) {
                    onNavigateTo({ view: View.Courts });
                } else if (onboardingStep === 2) {
                    onFetchUserProfile(); // reload courts for calendar
                    onNavigateTo({ view: View.Calendar });
                }
            }
        }
        if (!isCompany) {
            if (userRole === "owner") {
                if (onboardingStep === 0) {
                    onNavigateTo({ view: View.Club });
                } else if (onboardingStep === 1) {
                    onNavigateTo({ view: View.Members });
                } else if (onboardingStep === 2) {
                    onNavigateTo({ view: View.Courts });
                } else if (onboardingStep === 3) {
                    onFetchUserProfile(); // reload courts for calendar
                    onNavigateTo({ view: View.Calendar });
                } else if (onboardingStep === 4) {
                    setShowDesc(false);
                    onShowEndDemoDialog();
                } else if (onboardingStep === 5) {
                    onNavigateTo({ view: View.Club, tab: 3 }); // ownership tab
                } else if (onboardingStep === 6) {
                    onNavigateTo({ view: View.Club, tab: 3 }); // ownership tab
                }
            } else if (userRole === "admin") {
                if (onboardingStep === 0) {
                    onNavigateTo({ view: View.Members });
                } else if (onboardingStep === 1) {
                    onNavigateTo({ view: View.Courts });
                } else if (onboardingStep === 2) {
                    onFetchUserProfile(); // reload courts for calendar
                    onNavigateTo({ view: View.Calendar });
                }
            }
        }
    };

    // handle next click
    const onNextClick = async () => {
        if (userRole === "owner" && onboardingStep === 3 && club.clubStatus !== "demo") {
            await patchSettings({ onboardingStep: onboardingStep + 2 }); // skip demo step if already live
        } else {
            await patchSettings({ onboardingStep: onboardingStep + 1 });
        }
        await onFetchUserProfile();
        setShowDesc(false);
    };

    // render
    return (
        <>
            {isCompany ? (
                <Box style={{ textAlign: "center", padding: "16px 0" }}>
                    <Typography variant="h6">{i18n.onboarding}</Typography>
                    <LinearProgress variant="determinate" value={10 + (userRole === "owner" ? 24 : 40) * onboardingStep} style={{ borderRadius: "4px", height: "8px", margin: "8px 16px" }} />
                    {showDesc && i18n.companyOnboardingStepsDesc[userRole][onboardingStep] ? (
                        <>
                            <Typography variant="body2" align="left" style={{ padding: "16px" }}>
                                {i18n.companyOnboardingStepsDesc[userRole][onboardingStep]}
                            </Typography>
                            <Button style={{ margin: "8px", marginBottom: 0 }} onClick={onNextClick}>
                                {i18n.allRight}
                            </Button>
                        </>
                    ) : (
                        <Button style={{ margin: "8px", marginBottom: 0 }} onClick={onStepClick} color="primary" variant="contained">
                            {i18n.companyOnboardingSteps[userRole][onboardingStep]}
                        </Button>
                    )}
                </Box>
            ) : (
                <Box style={{ textAlign: "center", padding: "16px 0" }}>
                    <Typography variant="h6">{i18n.onboarding}</Typography>
                    <LinearProgress variant="determinate" value={10 + (userRole === "owner" ? 13 : 40) * onboardingStep} style={{ borderRadius: "4px", height: "8px", margin: "8px 16px" }} />
                    {showDesc && i18n.onboardingStepsDesc[userRole][onboardingStep] ? (
                        <>
                            <Typography variant="body2" align="left" style={{ padding: "16px" }}>
                                {i18n.onboardingStepsDesc[userRole][onboardingStep]}
                            </Typography>
                            <Button style={{ margin: "8px", marginBottom: 0 }} onClick={onNextClick}>
                                {i18n.allRight}
                            </Button>
                        </>
                    ) : (
                        <Button style={{ margin: "8px", marginBottom: 0 }} onClick={onStepClick} color="primary" variant="contained">
                            {i18n.onboardingSteps[userRole][onboardingStep]}
                        </Button>
                    )}
                </Box>
            )}
        </>
    );
};

export default Onboarding;
